import { I18n } from "@assets/strings";
import React, { Ref, useMemo } from "react";
import { Draggable } from "react-beautiful-dnd";
import styled from "styled-components";
import { ChapterDraft } from "@domain/lms/course/courseDraft";
import { BigPlusButton } from "@ui/components/buttons/bigPlusButton";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { TitledBlock } from "@ui/components/containers/titledBlock";
import { MaterialTextInput } from "@ui/components/inputs/materialTextInput";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { gap } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { AddSectionMenu } from "./addSectionMenu";
import {
	ChapterControllerContext,
	useEditorController,
} from "@ui/pages/lms/courseEditorPage/courseEditorController/editorControllerContext";
import { ExerciseEditorWrapper } from "@ui/pages/lms/courseEditorPage/exerciseEditor/exerciseEditorWrapper";
import { SectionEditor } from "@ui/pages/lms/courseEditorPage/sectionEditor/sectionEditor";

interface ChapterEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	chapter: ChapterDraft;
	droppableRef: Ref<HTMLDivElement>;
	sectionPlaceholder?: React.ReactNode;
}
export const ChapterEditor: React.FC<ChapterEditorProps> = ({
	chapter,
	droppableRef,
	sectionPlaceholder,
	...htmlProps
}) => {
	const courseController = useEditorController();
	const chapterController = useMemo(() => courseController.chapterController(chapter), [chapter]);
	const currentlyEditing = !!chapter.exercises.find((ex) => !!ex.editing);

	return (
		<ChapterControllerContext.Provider value={chapterController}>
			<Container {...htmlProps} ref={droppableRef}>
				<ChapterTitleEditor
					placeholder={I18n.t("course.editor.chapter.placeholder")}
					value={chapter.title}
					onChange={(v) => chapterController.setTitle(v)}
				/>
				{chapter.sections.map((section, index) => (
					<Draggable key={section.id} draggableId={section.id} index={index}>
						{(provided, snapshot) => (
							<div ref={provided.innerRef} {...provided.draggableProps}>
								<SectionEditor
									section={section}
									style={snapshot.isDragging ? { boxShadow: "0 22px 94px 0 rgba(0, 0, 0, 0.15)" } : {}}
								/>
								<DragIcon {...provided.dragHandleProps}>
									<div />
									<div />
								</DragIcon>
							</div>
						)}
					</Draggable>
				))}
				{sectionPlaceholder}
				<AddSectionMenu currentlyEditing={false} asFirst={chapter.sections.isEmpty()} />
				{chapter.exercises.size > 0 && (
					<Exercises title={I18n.t("course.section.qcm.title")}>
						{chapter.exercises.map((exercise, index) => (
							<ExerciseEditorWrapper key={exercise.id} exercise={exercise} index={index} />
						))}
						{!currentlyEditing && (
							<BigPlusButton label={I18n.t("course.editor.qcm.add")} onClick={() => chapterController.addExercise()} />
						)}
					</Exercises>
				)}
				{chapter.sections.size < 1 && chapter.exercises.size < 1 && (
					<ChapterMenu blur>
						<SimpleMenuContent onDelete={() => chapterController.removeChapter()} />
					</ChapterMenu>
				)}
			</Container>
		</ChapterControllerContext.Provider>
	);
};

const Container = styled(RoundedBlock)`
	position: relative;
	padding: 36px 50px 50px;
	${gap(50)};
	@media (max-width: 900px) {
		padding: 20px 25px 20px 20px;
		${gap(20)};
	}
`;

const Exercises = styled(TitledBlock)`
	${gap(40)};
	@media (max-width: 900px) {
		padding: 30px 25px 30px 15px;
		${gap(20)}
	}
`;

const ChapterTitleEditor = styled(MaterialTextInput)`
	input,
	input::placeholder {
		font-weight: 600;
		font-size: 1.5625rem;
		@media (max-width: 900px) {
			font-size: 1.25rem;
		}
	}
`;

const ChapterMenu = styled(KebabMenu)`
	position: absolute;
	right: 15px;
	top: 15px;
	z-index: ${Z.over};
`;

const DragIcon = styled.div`
	width: 30px;
	padding: 5px;
	${gap(5)};

	margin: 5px auto;
	> div {
		background-color: ${({ theme }) => theme.colors.lightgray};
		height: 1px;
	}
`;
